/**
 * Alpine.js collapsible data tree – morph-safe.
 *
 * Expanded paths are stored in a global Alpine store so they
 * survive Livewire re-renders / Reverb-triggered morphs.
 *
 * Usage:
 *   <div x-data="dataTree({ data: { ... }, id: 'step-42-output' })"
 *        class="dt-root ...">
 *       <div x-ref="tree"></div>
 *   </div>
 */
document.addEventListener('alpine:init', () => {

    /* ── Global store: persists across Livewire morphs ── */
    Alpine.store('dtState', { paths: {} });

    Alpine.data('dataTree', ({ data = {}, id = '_default', defaultDepth = 1 }) => ({
        data,
        id,
        defaultDepth,

        init() {
            const store = Alpine.store('dtState');
            if (!store.paths[this.id]) {
                store.paths[this.id] = {};
                this._autoExpand(this.data, '', 0);
            }
            this._render();

            // Event delegation for toggle buttons
            this.$el.addEventListener('click', (e) => {
                const btn = e.target.closest('[data-dt]');
                if (!btn) return;
                e.preventDefault();
                const path = btn.dataset.dt;
                const state = Alpine.store('dtState').paths[this.id];
                state[path] = !state[path];
                this._render();
            });
        },

        /* ── Helpers ── */

        _isOpen(path) {
            return !!Alpine.store('dtState').paths[this.id]?.[path];
        },

        _autoExpand(val, prefix, depth) {
            if (depth >= this.defaultDepth || val === null || typeof val !== 'object') return;
            const rootPath = prefix || '@';
            Alpine.store('dtState').paths[this.id][rootPath] = true;
            const keys = Array.isArray(val) ? val.map((_, i) => String(i)) : Object.keys(val);
            for (const k of keys) {
                const p = prefix ? prefix + '.' + k : k;
                if (typeof val[k] === 'object' && val[k] !== null) {
                    Alpine.store('dtState').paths[this.id][p] = true;
                    this._autoExpand(val[k], p, depth + 1);
                }
            }
        },

        _render() {
            this.$refs.tree.innerHTML = this._node(this.data, '', 0);
        },

        _node(value, path, depth) {
            if (value === null || value === undefined)
                return '<span class="dt-null">null</span>';
            if (typeof value === 'boolean')
                return '<span class="dt-bool">' + (value ? 'true' : 'false') + '</span>';
            if (typeof value === 'number')
                return '<span class="dt-num">' + value + '</span>';
            if (typeof value === 'string')
                return this._str(value);

            if (Array.isArray(value)) {
                if (value.length === 0) return '<span class="dt-muted">[]</span>';
                return this._collapsible(
                    path || '@',
                    '[' + value.length + ']',
                    value.map((v, i) => {
                        const cp = path ? path + '.' + i : String(i);
                        return '<div class="dt-row">'
                            + '<span class="dt-idx">' + i + '</span> '
                            + this._node(v, cp, depth + 1)
                            + '</div>';
                    }).join('')
                );
            }

            if (typeof value === 'object') {
                const keys = Object.keys(value);
                if (keys.length === 0) return '<span class="dt-muted">{}</span>';
                return this._collapsible(
                    path || '@',
                    '{' + keys.length + '}',
                    keys.map(k => {
                        const cp = path ? path + '.' + k : k;
                        return '<div class="dt-row">'
                            + '<span class="dt-key">' + this._esc(k) + '</span>'
                            + '<span class="dt-sep">: </span>'
                            + this._node(value[k], cp, depth + 1)
                            + '</div>';
                    }).join('')
                );
            }

            return this._esc(String(value));
        },

        _collapsible(path, summary, children) {
            const open = this._isOpen(path);
            return '<span class="dt-toggle" data-dt="' + this._esc(path) + '">'
                + '<span class="dt-arrow">' + (open ? '▼' : '▶') + '</span>'
                + '<span class="dt-summary">' + summary + '</span>'
                + '</span>'
                + (open
                    ? '<div class="dt-children">' + children + '</div>'
                    : '');
        },

        _str(value) {
            const escaped = this._esc(value);
            if (value.length <= 300) {
                return '<span class="dt-str">"' + escaped + '"</span>';
            }
            // Truncated with inline expand
            const uid = 'dt_s_' + this.id + '_' + Math.random().toString(36).substr(2, 6);
            return '<span class="dt-str">"'
                + '<span id="' + uid + '_p">' + this._esc(value.substring(0, 200)) + '…</span>'
                + '<span id="' + uid + '_f" style="display:none">' + escaped + '</span>'
                + '"</span> '
                + '<button type="button" onclick="document.getElementById(\'' + uid + '_p\').style.display=\'none\';'
                + 'document.getElementById(\'' + uid + '_f\').style.display=\'inline\';this.style.display=\'none\'"'
                + ' class="dt-more">[+' + (value.length - 200) + ']</button>';
        },

        _esc(s) {
            return String(s)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;');
        },
    }));
});
